import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog.js';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { Textarea } from '@/components/ui/textarea.js';
import { Label } from '@/components/ui/label.js';
import { Plus, X, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext.js';
import type { PromptArgument } from '@/types/prompt.js';

interface CreatePromptModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function CreatePromptModal({ open, onOpenChange, onSuccess }: CreatePromptModalProps) {
  const { api } = useAuth();
  const [name, setName] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [content, setContent] = useState('');
  const [args, setArgs] = useState<PromptArgument[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAddArgument = () => {
    setArgs([...args, { name: '', description: '', required: false }]);
  };

  const handleRemoveArgument = (index: number) => {
    setArgs(args.filter((_, i) => i !== index));
  };

  const handleArgumentChange = (
    index: number,
    field: keyof PromptArgument,
    value: string | boolean
  ) => {
    const newArgs = [...args];
    newArgs[index] = { ...newArgs[index], [field]: value };
    setArgs(newArgs);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate name (kebab-case)
    if (!/^[a-z0-9-]+$/.test(name)) {
      setError('Name must be kebab-case (lowercase letters, numbers, and hyphens only)');
      return;
    }

    if (!content.trim()) {
      setError('Content is required');
      return;
    }

    try {
      setLoading(true);

      // Filter out empty arguments
      const validArgs = args.filter((arg) => arg.name.trim());

      await api.createCustomPrompt({
        name,
        title: title.trim() || undefined,
        description: description.trim() || undefined,
        content: content.trim(),
        arguments: validArgs.length > 0 ? validArgs : undefined,
      });

      // Reset form
      setName('');
      setTitle('');
      setDescription('');
      setContent('');
      setArgs([]);
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      console.error('Failed to create prompt:', err);
      setError(err instanceof Error ? err.message : 'Failed to create prompt');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Custom Prompt</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
              {error}
            </div>
          )}

          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name">
              Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="name"
              placeholder="my-prompt-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="font-mono"
            />
            <p className="text-xs text-muted-foreground">
              Lowercase letters, numbers, and hyphens only (e.g., code-review, analyze-data)
            </p>
          </div>

          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">Title (Optional)</Label>
            <Input
              id="title"
              placeholder="My Prompt"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">A human-readable title for the prompt</p>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description (Optional)</Label>
            <Input
              id="description"
              placeholder="What this prompt does"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">A brief description of what the prompt does</p>
          </div>

          {/* Content */}
          <div className="space-y-2">
            <Label htmlFor="content">
              Prompt Content <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="content"
              placeholder="Your prompt content here. Use {{argName}} for named arguments or $1, $2 for positional arguments."
              value={content}
              onChange={(e) => setContent(e.target.value)}
              required
              rows={8}
              className="font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground">
              Use <code className="text-xs bg-muted px-1 rounded">{'{{argName}}'}</code> for named
              arguments or <code className="text-xs bg-muted px-1 rounded">$1, $2</code> for positional
              arguments
            </p>
          </div>

          {/* Arguments */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Arguments (Optional)</Label>
              <Button type="button" size="sm" variant="outline" onClick={handleAddArgument}>
                <Plus className="h-3.5 w-3.5 mr-1" />
                Add Argument
              </Button>
            </div>

            {args.length > 0 && (
              <div className="space-y-3 p-3 border rounded-lg">
                {args.map((arg, index) => (
                  <div key={index} className="flex gap-2 items-start">
                    <div className="flex-1 grid grid-cols-2 gap-2">
                      <Input
                        placeholder="Argument name"
                        value={arg.name}
                        onChange={(e) => handleArgumentChange(index, 'name', e.target.value)}
                        className="font-mono text-sm"
                      />
                      <Input
                        placeholder="Description"
                        value={arg.description || ''}
                        onChange={(e) => handleArgumentChange(index, 'description', e.target.value)}
                        className="text-sm"
                      />
                    </div>
                    <label className="flex items-center gap-1.5 text-sm whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={arg.required || false}
                        onChange={(e) => handleArgumentChange(index, 'required', e.target.checked)}
                        className="rounded"
                      />
                      Required
                    </label>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      onClick={() => handleRemoveArgument(index)}
                      className="flex-shrink-0"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Define arguments that users can pass when using this prompt
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading || !name.trim() || !content.trim()}>
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Prompt'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
